import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from '@/context/AppContext';
import useChromeLayer from '@/hooks/useChromeLayer';
import { iconBoxStyle } from '@/theme/icons';
import { moduleBox } from '@/theme/header-modules';
import { MEDIA_FRAME, MEDIA_TITLE, mediaChromeActive, mediaFrameStyle } from '@/theme/media-chrome';
import { videoTextStyle } from '@/theme/video-chrome';
import { pageTitleStyle, titleWithPageLook } from '@/theme/viewer-look';

/** The open page's own viewer colours, set by the viewer around its chrome. */
export const ViewerLookContext = createContext(null);

const CONTAINED = [
    'flex min-h-0 min-w-0 items-center justify-center overflow-hidden',
    '[&_img]:!static [&_img]:!h-full [&_img]:!w-full [&_img]:!max-h-full [&_img]:!max-w-full',
    '[&_img]:!min-h-0 [&_img]:!min-w-0 [&_img]:!transform-none [&_img]:!object-contain',
    '[&_video]:!static [&_video]:!h-full [&_video]:!w-full [&_video]:!max-h-full [&_video]:!max-w-full',
    '[&_video]:!min-h-0 [&_video]:!min-w-0 [&_video]:!transform-none [&_video]:!object-contain',
].join(' ');

/**
 * The open picture or video, inset from the viewport by horizontal and vertical
 * margins. The file is always fully visible inside that box.
 */
export default function MediaFrame({ enabled = true, className, style, children }) {
    const { theme } = useAppContext();
    const layer = useChromeLayer();
    const box = enabled ? moduleBox(theme?.header?.modules, MEDIA_FRAME, layer) : null;
    return (
        <div
            className={enabled ? CONTAINED : className}
            style={enabled ? mediaFrameStyle(box) : style}
            data-header-module={enabled ? MEDIA_FRAME : undefined}
            data-lock-ratio="0"
        >
            {children}
        </div>
    );
}

MediaFrame.propTypes = {
    enabled: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    children: PropTypes.node,
};

/**
 * Title styling shared by the image caption and the video caption.
 * Video falls back to a previously placed video-caption box.
 */
export function useMediaTitle({ videoFallback = false } = {}) {
    const { theme } = useAppContext();
    const pageLook = useContext(ViewerLookContext);
    const layer = useChromeLayer();
    const modules = theme?.header?.modules;
    const title = moduleBox(modules, MEDIA_TITLE, layer);
    const caption = videoFallback ? moduleBox(modules, 'video-caption', layer) : null;
    const source = mediaChromeActive(title)
        ? { id: MEDIA_TITLE, box: title }
        : (videoFallback && mediaChromeActive(caption) ? { id: 'video-caption', box: caption } : { id: MEDIA_TITLE, box: title });
    const active = mediaChromeActive(source.box);
    const placed = source.box?.anchor === 'free';
    const typeStyle = active
        ? videoTextStyle(titleWithPageLook(source.box, pageLook), theme)
        : pageTitleStyle(pageLook, source.box);
    return {
        id: source.id,
        box: source.box,
        active,
        placed,
        style: {
            ...(typeStyle || {}),
            ...(placed ? iconBoxStyle(source.box) : {}),
        },
    };
}
